import { useCallback } from "react";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { DataManagementEvents } from "@utils/track-event/track-event-list";
import { UploadElementType } from "@custom-types/file-upload-types";

export enum UploadFilesErrorToastType {
  createRevision = "createRevision",
  sendMutation = "sendMutation",
  uploadMetadataFiles = "uploadMetadataFiles",
  uploadBlinkFiles = "uploadBlinkFiles",
  uploadOrbisFiles = "uploadOrbisFiles",
  uploadFocusFiles = "uploadFocusFiles",
  uploadPhotogrammetryFiles = "uploadPhotogrammetryFiles",
}

type ReturnFunction = (
  errorType: UploadFilesErrorToastType,
  type: UploadElementType,
) => void;

/** Tracks an Amplitude event for a failed file upload. */
export function useUploadFilesTrackError(): ReturnFunction {
  const { trackEvent } = useTrackEvent();

  return useCallback(
    (errorType: UploadFilesErrorToastType, type: UploadElementType): void => {
      let message: string;
      switch (errorType) {
        case UploadFilesErrorToastType.createRevision:
          message = "Failed to prepare the data import. Please try again.";
          break;
        case UploadFilesErrorToastType.uploadMetadataFiles:
        case UploadFilesErrorToastType.uploadBlinkFiles:
        case UploadFilesErrorToastType.uploadOrbisFiles:
        case UploadFilesErrorToastType.uploadFocusFiles:
        case UploadFilesErrorToastType.uploadPhotogrammetryFiles:
          message = "Failed to upload the data. Please try again.";
          break;
        case UploadFilesErrorToastType.sendMutation:
          message = "Failed to finalize the data import. Please try again.";
          break;
        default:
          errorType satisfies never;
          message = "Failed to upload the data. Please try again.";
          break;
      }

      trackEvent({
        name: DataManagementEvents.uploadFilesError,
        props: {
          type,
          error: errorType,
          message,
        },
      });
    },
    [trackEvent]
  );
}
